import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, IconButton, Box, InputAdornment, Typography } from '@mui/material';
import { QrCodeScanner as BarcodeIcon } from '@mui/icons-material';
import CameraScannerDialog from './CameraScannerDialog';

/**
 * ScanInput - Unified barcode/QR scanning component
 * Supports both Zebra scanner (keyboard wedge) and phone camera
 * Provides audio and haptic feedback on scan
 */
export default function ScanInput({
  onScan,
  placeholder = "Scan barcode or QR code...",
  disabled = false,
  autoFocus = true
}) {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const inputRef = useRef(null);
  const audioContextRef = useRef(null);

  // Audio feedback - plays beep on successful/failed scan
  const playBeep = (success = true) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      // High frequency = success, low frequency = error
      oscillator.frequency.value = success ? 1200 : 400;
      oscillator.type = 'sine';

      // Short beep
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.1);

      oscillator.start();
      oscillator.stop(audioContextRef.current.currentTime + 0.1);
    } catch (error) {
      console.warn('Audio feedback failed:', error);
    }
  };

  // Haptic feedback (mobile only)
  const vibrate = (pattern = [50]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Handle scan from either keyboard input or camera
  const handleScan = async (barcode) => {
    if (!barcode || !barcode.trim()) return;

    let trimmedBarcode = barcode.trim();

    // Check if this is a job URL (e.g., /jobs/6667 or full URL with /jobs/6667)
    try {
      let jobIdMatch = null;

      // Check for direct path pattern: /jobs/6667
      if (trimmedBarcode.match(/^\/jobs\/(\d+)/)) {
        jobIdMatch = trimmedBarcode.match(/^\/jobs\/(\d+)/);
      }
      // Check for full URL pattern
      else if (trimmedBarcode.startsWith('http://') || trimmedBarcode.startsWith('https://')) {
        const url = new URL(trimmedBarcode);
        const pathMatch = url.pathname.match(/^\/jobs\/(\d+)/);
        if (pathMatch) {
          jobIdMatch = pathMatch;
        } else {
          // Check for equipment_id parameter (existing equipment QR code logic)
          const equipmentId = url.searchParams.get('equipment_id');
          if (equipmentId) {
            // Use equipment_id instead of full URL
            trimmedBarcode = equipmentId;
            console.log('Extracted equipment_id from scanned URL:', equipmentId);
          }
        }
      }

      // If we found a job URL, navigate to that job page
      if (jobIdMatch) {
        const jobId = jobIdMatch[1];
        console.log('Detected job URL, navigating to job:', jobId);
        playBeep(true);
        vibrate([50]);
        navigate(`/jobs/${jobId}`);
        setValue('');
        return; // Exit early, don't call onScan
      }
    } catch (err) {
      // Not a URL or parsing failed, continue with normal flow
      console.log('URL parsing failed, using raw value:', err);
    }

    try {
      // Call parent's onScan handler with extracted equipment_id
      const result = await onScan(trimmedBarcode);

      // Provide feedback based on result
      if (result !== false) { // Success if not explicitly false
        playBeep(true);
        vibrate([50]); // Short vibration
      } else {
        playBeep(false);
        vibrate([100, 50, 100]); // Error pattern: long-short-long
      }
    } catch (error) {
      console.error('Scan error:', error);
      playBeep(false);
      vibrate([100, 50, 100]);
    }

    // Clear input and refocus for next scan
    setValue('');
    inputRef.current?.focus();
  };

  // Auto-focus on mount if enabled
  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  // Handle keyboard input (Zebra scanner acts like keyboard)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScan(value);
    }
  };

  return (
    <>
      <TextField
        inputRef={inputRef}
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BarcodeIcon
                sx={{ color: disabled ? 'action.disabled' : 'action.active' }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowCamera(true)}
                disabled={disabled}
                edge="end"
                sx={{
                  gap: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                    color: 'text.secondary'
                  }}
                >
                  📷 <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Use Camera</Box>
                </Typography>
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '1.1rem',
            '& input': {
              fontFamily: 'monospace', // Better for barcodes
              letterSpacing: '0.5px'
            }
          }
        }}
      />

      {/* Camera Scanner Modal */}
      {showCamera && (
        <CameraScannerDialog
          open={showCamera}
          onScan={(barcode) => {
            handleScan(barcode);
            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </>
  );
}
